import { useBusinessContext } from '@context'
import { useQueryParam } from '@hooks'
import { isBusinessBillingLocked } from '@query'
import { motion } from 'framer-motion'
import React from 'react'
import styled from 'styled-components'
import { BasicSideSheet } from '../components/BasicSideSheet'
import { MainMenu } from '../containers/MainMenu'
import useScreen from '../utils/useScreen'
import { AIHelpSheet } from '@containers/AIHelpSheet/AIHelpSheet.tsx'
import { GetStartedWidget } from '@containers/GetStartedWidget'

export const Page: React.FC<{ head?: React.ReactNode; children: React.ReactNode }> = ({
  head,
  children
}) => {
  const { data: business } = useBusinessContext()
  const isBillingLocked = isBusinessBillingLocked(business)

  return (
    <StyledPageWrapper>
      <StyledMainColumn>
        {head}

        <StyledPage>
          <ResponsiveMenu />
          <ContentWrapper>{children}</ContentWrapper>
        </StyledPage>
      </StyledMainColumn>

      {!isBillingLocked && <AIHelpSheet />}
      {!isBillingLocked && <GetStartedWidget />}
    </StyledPageWrapper>
  )
}

const ResponsiveMenu: React.FC = () => {
  const { isMobile } = useScreen()
  const [isMenuOpen, setMenuOpen] = useQueryParam<string>('menu')

  if (isMobile)
    return (
      <BasicSideSheet
        heading="Menu"
        position="left"
        isShown={isMenuOpen === 'true'}
        onClose={() => setMenuOpen(null)}
      >
        <MainMenu width="100%" />
      </BasicSideSheet>
    )

  return <MainMenu isTogglable={true} />
}

const StyledPage = styled(motion.div).attrs({
  initial: false,
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -5 },
  transition: { type: 'tween' }
})`
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
`

const ContentWrapper = styled.div`
  flex: 1;
  z-index: 0;
  overflow: auto;
`

/** Banners live here so they never take height away from the AI help sheet. */
const StyledMainColumn = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  > [data-page-banner='demo'] + ${StyledPage} .page-layout-wrapper,
  > [data-page-banner='billing-lock'] + ${StyledPage} .page-layout-wrapper {
    border-radius: 0 0 0 ${({ theme }) => theme.borderRadius.md}rem;

    @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
      border-radius: 0;
    }
  }
`

const StyledPageWrapper = styled(motion.div).attrs({
  initial: false,
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -5 },
  transition: { type: 'tween' }
})`
  height: 100dvh;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
`
